/**
 * Primitivos de interfaz.
 *
 * Deliberadamente pequeños y sin librería de componentes: la app tiene una
 * superficie acotada y una dependencia más costaría más de lo que ahorra.
 */

import { AlertTriangle, Inbox, Loader2, PencilLine, ShieldCheck } from 'lucide-react'
import type { ReactNode } from 'react'

import type { Nivel, Postura, TipoStakeholder } from '../api/tipos'
import {
  COLOR_TIPO,
  ETIQUETA_NIVEL,
  ETIQUETA_POSTURA,
  ETIQUETA_TIPO,
  ORDEN_NIVEL,
  VAR_POSTURA,
  conAlfa,
} from './paleta'
import { cx } from './util'

// --------------------------------------------------------------------------
// Botones
// --------------------------------------------------------------------------

type VarianteBoton = 'primario' | 'secundario' | 'sutil' | 'peligro'

const ESTILO_BOTON: Record<VarianteBoton, string> = {
  primario:
    'bg-[var(--color-acento)] text-white hover:brightness-110 active:brightness-95 border-transparent',
  secundario:
    'bg-[var(--color-panel)] text-[var(--color-texto)] border-[var(--color-borde-fuerte)] hover:bg-[var(--color-acento-suave)]',
  sutil:
    'bg-transparent text-[var(--color-texto-suave)] border-transparent hover:bg-[var(--color-acento-suave)] hover:text-[var(--color-texto)]',
  peligro:
    'bg-transparent text-[var(--color-opositora)] border-[var(--color-opositora)] hover:bg-[color-mix(in_srgb,var(--color-opositora)_12%,transparent)]',
}

export function Boton({
  variante = 'secundario',
  cargando = false,
  icono,
  children,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variante?: VarianteBoton
  cargando?: boolean
  icono?: ReactNode
}) {
  return (
    <button
      {...props}
      disabled={props.disabled || cargando}
      className={cx(
        'inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-1.5',
        'text-[13px] font-medium transition-[background-color,filter] duration-150',
        'disabled:cursor-not-allowed disabled:opacity-50',
        ESTILO_BOTON[variante],
        className,
      )}
    >
      {cargando ? <Loader2 size={14} className="animate-spin" /> : icono}
      {children}
    </button>
  )
}

// --------------------------------------------------------------------------
// Etiquetas de dominio
// --------------------------------------------------------------------------

/** Punto de color + texto. Es el patrón que también usa el grafo. */
export function EtiquetaTipo({
  tipo,
  compacta,
}: {
  tipo: TipoStakeholder
  compacta?: boolean
}) {
  const color = COLOR_TIPO[tipo] ?? COLOR_TIPO.otro
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span
        aria-hidden
        className="size-2 shrink-0 rounded-full"
        style={{ background: color }}
      />
      {!compacta && (
        <span className="text-[var(--color-texto-suave)]">{ETIQUETA_TIPO[tipo] ?? tipo}</span>
      )}
    </span>
  )
}

export function EtiquetaPostura({ postura }: { postura: Postura }) {
  const color = VAR_POSTURA[postura] ?? VAR_POSTURA.desconocida
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap"
      style={{ background: conAlfa(color, 16), color }}
    >
      {ETIQUETA_POSTURA[postura] ?? postura}
    </span>
  )
}

/**
 * Nivel como escalera de cinco barras.
 *
 * Se prefiere a un texto porque la tabla se escanea en vertical: comparar
 * alturas es inmediato, leer «muy_alto» contra «alto» no lo es.
 */
export function EscaleraNivel({ nivel, titulo }: { nivel: Nivel; titulo?: string }) {
  const valor = ORDEN_NIVEL[nivel] ?? 2
  return (
    <span
      className="inline-flex items-end gap-[2px]"
      title={titulo ? `${titulo}: ${ETIQUETA_NIVEL[nivel]}` : ETIQUETA_NIVEL[nivel]}
      aria-label={ETIQUETA_NIVEL[nivel]}
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className={cx(
            'w-[3px] rounded-[1px] transition-colors',
            i <= valor ? 'bg-[var(--color-acento)]' : 'bg-[var(--color-borde)]',
          )}
          style={{ height: 4 + i * 2.5 }}
        />
      ))}
    </span>
  )
}

export function Chip({
  children,
  color,
  titulo,
}: {
  children: ReactNode
  color?: string
  titulo?: string
}) {
  return (
    <span
      title={titulo}
      className="inline-flex items-center rounded px-1.5 py-0.5 text-[11px] whitespace-nowrap"
      style={
        color
          ? { background: conAlfa(color, 14), color }
          : {
              background: 'var(--color-acento-suave)',
              color: 'var(--color-texto-suave)',
            }
      }
    >
      {children}
    </span>
  )
}

/** Marca un dato tocado a mano, para no confundirlo con salida de la IA (§7). */
export function MarcaEdicion({ campos }: { campos?: string[] }) {
  const detalle = campos?.length ? `Editado a mano: ${campos.join(', ')}` : 'Editado a mano'
  return (
    <span title={detalle} className="text-[var(--color-acento)]">
      <PencilLine size={13} aria-label={detalle} />
    </span>
  )
}

/** Visto en más de una corrida: señal de corroboración (§7). */
export function MarcaCorroborado({ corridas }: { corridas: number }) {
  const detalle = `Corroborado en ${corridas} corridas`
  return (
    <span title={detalle} style={{ color: 'var(--color-favorable)' }}>
      <ShieldCheck size={13} aria-label={detalle} />
    </span>
  )
}

// --------------------------------------------------------------------------
// Estados
// --------------------------------------------------------------------------

export function Cargando({ texto = 'Cargando…' }: { texto?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 p-8 text-[var(--color-texto-tenue)]">
      <Loader2 size={16} className="animate-spin" />
      <span className="text-[13px]">{texto}</span>
    </div>
  )
}

/** Esqueleto de tabla: ocupa el sitio final para que nada salte al cargar. */
export function EsqueletoTabla({
  filas = 8,
  columnas = 6,
}: {
  filas?: number
  columnas?: number
}) {
  return (
    <div className="p-1" aria-hidden>
      {Array.from({ length: filas }).map((_, f) => (
        <div key={f} className="flex gap-3 border-b border-[var(--color-borde)] px-3 py-2.5">
          {Array.from({ length: columnas }).map((_, c) => (
            <div
              key={c}
              className="pulso h-3 rounded bg-[var(--color-borde)]"
              style={{
                width: c === 0 ? '30%' : `${8 + ((f + c) % 4) * 3}%`,
                animationDelay: `${(f * columnas + c) * 24}ms`,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export function Vacio({
  titulo,
  detalle,
  accion,
  icono,
}: {
  titulo: string
  detalle?: string
  accion?: ReactNode
  icono?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      <span className="text-[var(--color-texto-tenue)]">{icono ?? <Inbox size={22} />}</span>
      <p className="text-[14px] font-medium">{titulo}</p>
      {detalle && (
        <p className="max-w-md text-[13px] text-[var(--color-texto-suave)]">{detalle}</p>
      )}
      {accion && <div className="mt-2">{accion}</div>}
    </div>
  )
}

export function Error({ error, reintentar }: { error: unknown; reintentar?: () => void }) {
  const mensaje =
    error instanceof globalThis.Error ? error.message : 'Ocurrió un error inesperado.'
  return (
    <div
      role="alert"
      className="m-3 rounded-md border border-[var(--color-opositora)] bg-[color-mix(in_srgb,var(--color-opositora)_7%,transparent)] p-4"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle size={16} className="mt-0.5 shrink-0 text-[var(--color-opositora)]" />
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-medium">No se pudo completar la operación</p>
          <p className="mt-1 text-[13px] break-words text-[var(--color-texto-suave)]">
            {mensaje}
          </p>
          {reintentar && (
            <Boton variante="secundario" className="mt-3" onClick={reintentar}>
              Reintentar
            </Boton>
          )}
        </div>
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------
// Contenedores
// --------------------------------------------------------------------------

export function Panel({
  titulo,
  acciones,
  children,
  className,
}: {
  titulo?: ReactNode
  acciones?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section
      className={cx(
        'overflow-hidden rounded-lg border border-[var(--color-borde)] bg-[var(--color-panel)]',
        className,
      )}
    >
      {(titulo || acciones) && (
        <header className="flex items-center justify-between gap-3 border-b border-[var(--color-borde)] px-3 py-2">
          <div className="text-[13px] font-semibold">{titulo}</div>
          {acciones && <div className="flex items-center gap-1.5">{acciones}</div>}
        </header>
      )}
      {children}
    </section>
  )
}

export function Campo({
  etiqueta,
  ayuda,
  children,
}: {
  etiqueta: string
  ayuda?: string
  children: ReactNode
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] font-medium text-[var(--color-texto-suave)]">
        {etiqueta}
      </span>
      {children}
      {ayuda && (
        <span className="mt-1 block text-[11px] text-[var(--color-texto-tenue)]">{ayuda}</span>
      )}
    </label>
  )
}

// Sin `w-full`: el ancho lo decide quien lo usa. Ponerlo en la base hacía
// que un `w-auto` del sitio de uso no ganara, porque a igual especificidad
// manda el orden del CSS generado, no el de la cadena de clases.
const ESTILO_ENTRADA =
  'rounded-md border border-[var(--color-borde-fuerte)] bg-[var(--color-panel)] px-2.5 py-1.5 text-[13px] text-[var(--color-texto)] placeholder:text-[var(--color-texto-tenue)] disabled:opacity-60'

export function Entrada(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(ESTILO_ENTRADA, 'w-full', props.className)} />
}

export function AreaTexto(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea {...props} className={cx(ESTILO_ENTRADA, 'w-full resize-y', props.className)} />
  )
}

export function Seleccion(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cx(ESTILO_ENTRADA, 'pr-7', props.className)} />
}

export function Interruptor({
  activo,
  onChange,
  children,
}: {
  activo: boolean
  onChange: (valor: boolean) => void
  children: ReactNode
}) {
  return (
    <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12px] text-[var(--color-texto-suave)] select-none">
      <input
        type="checkbox"
        checked={activo}
        onChange={(e) => onChange(e.target.checked)}
        className="size-3.5 accent-[var(--color-acento)]"
      />
      {children}
    </label>
  )
}

/** Contador discreto junto al nombre de una pestaña. */
export function Contador({ valor, destacado }: { valor: number; destacado?: boolean }) {
  if (!valor) return null
  return (
    <span
      className={cx(
        'ml-1 rounded-full px-1.5 py-px text-[10px] font-semibold tabular',
        destacado
          ? 'bg-[var(--color-opositora)] text-white'
          : 'bg-[var(--color-acento-suave)] text-[var(--color-texto-suave)]',
      )}
    >
      {valor}
    </span>
  )
}
